/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Events Service - Domain Event Dashboard API
 * ============================================================================
 *
 * Service layer for the domain event dashboard API calls.
 * Provides event listener management, metrics, and monitoring.
 *
 * ============================================================================
 */

import { get, post } from '@/services/api/client';
import { ApiResponse } from '@/services/api/types';

const BASE_URL = '/admin/events';

// Types

export interface EventListenerInfo {
  id: string;
  eventType: string;
  listenerClass: string;
  shortClassName: string;
  moduleCode: string;
  enabled: boolean;
  description: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface EventMetricsDto {
  eventType: string;
  date: string;
  totalReceived: number;
  totalProcessed: number;
  totalFailed: number;
  avgProcessingTimeMs: number;
  minProcessingTimeMs: number | null;
  maxProcessingTimeMs: number | null;
  lastReceivedAt: string | null;
  lastProcessedAt: string | null;
  lastErrorAt: string | null;
  lastErrorMessage: string | null;
  successRate: number;
  failureRate: number;
}

export interface EventDashboardSummary {
  totalReceived: number;
  totalProcessed: number;
  totalFailed: number;
  queueDepth: number;
  processingCount: number;
  enabledListeners: number;
  disabledListeners: number;
  lastUpdatedAt: string | null;
  listeners: EventListenerInfo[];
}

export interface ToggleResult {
  eventType: string;
  enabled: boolean;
  persisted: boolean;
}

/**
 * Events service for domain event dashboard
 */
export const eventsService = {
  /**
   * Get dashboard summary with all stats and listeners
   */
  async getDashboardSummary(): Promise<EventDashboardSummary> {
    const response = await get<ApiResponse<EventDashboardSummary>>(`${BASE_URL}/summary`);
    return response.data;
  },

  /**
   * Get all registered event listeners
   */
  async getAllListeners(): Promise<EventListenerInfo[]> {
    const response = await get<ApiResponse<EventListenerInfo[]>>(BASE_URL);
    return response.data;
  },

  /**
   * Get listeners by module
   */
  async getListenersByModule(moduleCode: string): Promise<EventListenerInfo[]> {
    const response = await get<ApiResponse<EventListenerInfo[]>>(`${BASE_URL}/module/${moduleCode}`);
    return response.data;
  },

  /**
   * Get all module codes
   */
  async getModuleCodes(): Promise<string[]> {
    const response = await get<ApiResponse<string[]>>(`${BASE_URL}/modules`);
    return response.data;
  },

  /**
   * Get all event metrics for today
   */
  async getAllMetrics(): Promise<EventMetricsDto[]> {
    const response = await get<ApiResponse<EventMetricsDto[]>>(`${BASE_URL}/metrics`);
    return response.data;
  },

  /**
   * Get metrics for a specific event type
   */
  async getEventMetrics(eventType: string): Promise<EventMetricsDto> {
    const response = await get<ApiResponse<EventMetricsDto>>(`${BASE_URL}/${eventType}/metrics`);
    return response.data;
  },

  /**
   * Get trend data for an event type
   */
  async getEventTrend(eventType: string, days: number = 7): Promise<EventMetricsDto[]> {
    const response = await get<ApiResponse<EventMetricsDto[]>>(
      `${BASE_URL}/${eventType}/trend`,
      { days }
    );
    return response.data;
  },

  /**
   * Enable a listener for an event type
   */
  async enableListener(
    eventType: string,
    listenerClass?: string,
    persist: boolean = true
  ): Promise<ToggleResult> {
    const params: Record<string, unknown> = { persist };
    if (listenerClass) {
      params.listenerClass = listenerClass;
    }
    const response = await post<ApiResponse<ToggleResult>>(
      `${BASE_URL}/${eventType}/enable`,
      null,
      params
    );
    return response.data;
  },

  /**
   * Disable a listener for an event type
   */
  async disableListener(
    eventType: string,
    listenerClass?: string,
    persist: boolean = true
  ): Promise<ToggleResult> {
    const params: Record<string, unknown> = { persist };
    if (listenerClass) {
      params.listenerClass = listenerClass;
    }
    const response = await post<ApiResponse<ToggleResult>>(
      `${BASE_URL}/${eventType}/disable`,
      null,
      params
    );
    return response.data;
  },

  /**
   * Toggle a listener for an event type
   */
  async toggleListener(
    eventType: string,
    listenerClass?: string,
    persist: boolean = true
  ): Promise<ToggleResult> {
    const params: Record<string, unknown> = { persist };
    if (listenerClass) {
      params.listenerClass = listenerClass;
    }
    const response = await post<ApiResponse<ToggleResult>>(
      `${BASE_URL}/${eventType}/toggle`,
      null,
      params
    );
    return response.data;
  },

  /**
   * Clear the in-memory listener cache
   */
  async clearCache(): Promise<string> {
    const response = await post<ApiResponse<string>>(`${BASE_URL}/cache/clear`);
    return response.data;
  },
};

export default eventsService;
